import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MyAquilaApiService } from '../shared/services/my-aquila-api.service';
import { Permissions } from '../shared/dto/permissions';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private permissions = Permissions;
  private usersPermissions: BehaviorSubject<typeof Permissions | {}> =
    new BehaviorSubject({});

  /** localStorage key for the effective list (role + added - removed), which the login token does not carry. */
  private static readonly EFFECTIVE_KEY = 'userPermEffective';

  constructor(private myAquilaApi: MyAquilaApiService) {}

  /**
   * Reads GET /my-aquila/permissions (the caller's effective permissions, including per-person additions and
   * removals) and makes it the source for every permission check. Fail-soft: on an error the token's list stays.
   */
  public loadEffectivePermissions(): Observable<boolean> {
    return this.myAquilaApi.permissionsQuiet().pipe(
      tap(res => {
        const codes = (res?.modules || []).flatMap(module => (module.permissions || []).map(p => p.code)).filter(Boolean);
        localStorage.setItem(PermissionService.EFFECTIVE_KEY, JSON.stringify(codes));
        this.evaluateUsersPermissions();
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  public clearEffectivePermissions(): void {
    localStorage.removeItem(PermissionService.EFFECTIVE_KEY);
  }

  public get UsersPermissions() {
    return this.usersPermissions.value;
  }
  public get UsersPermissions$() {
    return this.usersPermissions.asObservable();
  }
  public setUsersPermissions(perm: typeof Permissions | {}) {
    this.usersPermissions.next(perm);
  }

  public evaluateUsersPermissions() {
    // Super users keep the token's list (it carries `super-user`); everyone else uses the effective list when loaded.
    const tokenPermissions: string[] = JSON.parse(localStorage.getItem('userPerm') || '[]');
    const effective = localStorage.getItem(PermissionService.EFFECTIVE_KEY);
    const permissions: string[] =
      effective && !tokenPermissions.includes(Permissions.SUPER_USER)
        ? JSON.parse(effective)
        : tokenPermissions;

    const permissionsObj: typeof Permissions | {} = {};
    const selectedPermissions = {};
    permissions.forEach((perm) => {
      const trimedPerm = perm.trim();
      selectedPermissions[trimedPerm] = trimedPerm;
    });

    Object.entries(this.permissions).forEach((val) => {
      if (selectedPermissions[val[1]]) {
        permissionsObj[val[1]] = true;
      }
    });

    this.setUsersPermissions(permissionsObj);
  }

  public hasPermissionList(perms: string[]): boolean {
    const permsList = [];
    if (!perms || !perms.length) return false;

    // Super users bypass all permission checks
    if (this.isSuperUser) return true;

    for (let i = 0; i < perms.length; i++) {
      if (perms[i] === Permissions.IS_FRANCHISEE) {
        return this.isFranchisee;
      }
      if (perms[i] === Permissions.NONE) return true;
      if (this.usersPermissions.value[perms[i]]) {
        permsList.push(perms[i]);
      }
    }
    if (permsList.length) return true;
    return false;
  }

  /** Whether the caller holds any permission code, including ones the app has no enum entry for. */
  public holdsCode(code: string): boolean {
    if (this.isSuperUser) return true;
    const tokenPermissions: string[] = JSON.parse(localStorage.getItem('userPerm') || '[]');
    const effective = localStorage.getItem(PermissionService.EFFECTIVE_KEY);
    const codes: string[] = effective ? JSON.parse(effective) : tokenPermissions;
    return codes.some(c => c.trim() === code);
  }

  public get isSuperUser(): boolean {
    return !!this.usersPermissions.value[Permissions.SUPER_USER];
  }

  private get isFranchisee(): boolean {
    // logic to evaluate if current user is a franchisee
    return false;
  }

  // public generatePerms() {
  //   const perms = [
  //     Permissions.ACTIVATE_CLIENT,
  //     Permissions.APPROVE_CLIENT,
  //     Permissions.RUN_LOAN_REPORTS,
  //     Permissions.APPROVE_CLIENT_LOAN_ACCOUNT,
  //   ];
  //   setTimeout(() => {
  //     localStorage.setItem('userPerm', JSON.stringify(perms));
  //     this.evaluateUsersPermissions();
  //   }, 10000);
  // }
}
